/**
 * Gardes de permission par URL — registre RBAC §5, trous n°3/4/5.
 *
 * Le Sidebar filtre déjà le menu via NAV_ITEMS (`permission` / `adminOnly` /
 * `restrictRoles`), mais retirer une page du menu ne protège pas son URL :
 * le routeur n'appliquait que RequireSession. Ce module applique les MÊMES
 * règles que le menu aux URLs :
 *   - routes présentes dans NAV_ITEMS → règle dérivée automatiquement ;
 *   - sous-pages absentes du menu → héritage explicite de la page liste ;
 *   - pages exclues de la matrice v2 (§8) → blocage pur (« Page retirée »),
 *     sauf OWNER/ADMIN (portes de service pendant la transition RBAC v2).
 *
 * Logique pure, testée dans route-permissions.test.ts ; le rendu est dans
 * RoutePermissionGuard.tsx. Rappel docs/security.md : ceci reste une garde
 * UX — le backend re-vérifie toujours (Phase 2 enforcement).
 */

import { NAV_ITEMS } from "@/lib/constants";
import { can, isAgencyAdmin, type CurrentUser } from "@/lib/permissions";

export interface RouteRule {
  /** permission key required (même clé que NAV_ITEMS) */
  permission?: string;
  /** when true, only OWNER/ADMIN/MANAGER pass (même sémantique que NAV_ITEMS) */
  adminOnly?: boolean;
  /** when set, user must have one of these roles (même sémantique que NAV_ITEMS) */
  restrictRoles?: string[];
  /**
   * Page exclue de la matrice RBAC v2 (§8) : blocage pur pour tous —
   * SAUF OWNER/ADMIN, portes de service pendant la transition RBAC v2.
   */
  removed?: boolean;
}

export type RouteAccess = "allowed" | "forbidden" | "removed";

/**
 * Pages exclues de la matrice v2 (§8) — trou n°3 « blocage pur » (les trous
 * n°4 `/statistics` et n°5 `/marketing/budget` + données financières
 * `/payments`/`/wallets`/`/expenses` sont couverts ici).
 * `/payments` et `/wallets` ne sont pas routés aujourd'hui (WalletsPage
 * orpheline — trou n°5) mais restent listés défensivement.
 *
 * `/subscriptions` en est SORTI : la page Abonnements est désormais une entrée
 * de menu Finance à part entière (spec « dernières modifications » §7), donc
 * elle suit la règle dérivée de NAV_ITEMS (`subscriptions.view` + rôles
 * Finance) comme les autres pages du groupe.
 */
// `/archives` en est SORTI : le Centre d'archives est redevenu une entrée de
// menu (groupe Équipe, restrictRoles OWNER/ADMIN) — sa règle dérive de
// NAV_ITEMS comme les autres pages.
const REMOVED_ROUTES = new Set<string>([
  "/campaigns",
  "/statistics",
  "/expenses",
  "/payments",
  "/wallets",
  "/compta/operations",
  "/compta/rapports",
  "/marketing/calendrier",
  "/marketing/budget",
  "/marketing/rapports",
  "/team/planning",
  "/team/performance",
  "/team/demandes",
]);

/**
 * Routes ABSENTES de NAV_ITEMS : la sous-page hérite de la page liste.
 *
 * - `/clients/:clientId` → hérite de la page liste `/clients` (clients.view)
 * - `/tasks/:taskId` → hérite de la page liste `/tasks` (tasks.view)
 * - `/team/:memberId` → devrait hériter de `/team` (team.view) SAUF
 *   auto-consultation ; or le store auth n'expose que l'id du compte
 *   (JWT `sub`), pas l'id du membre d'équipe (`member.userId` n'est connu
 *   qu'après avoir chargé la fiche). Impossible de détecter le « soi-même »
 *   côté routeur sans fetch → on laisse passer et le backend décide (§5
 *   trou n°2, enforcement Phase 2).
 * - `/profile`, `/notifications` → pages personnelles, aucune garde (non
 *   routées aujourd'hui ; listées défensivement).
 */
const EXTRA_ROUTE_RULES: Record<string, RouteRule> = {
  "/clients/:clientId": { permission: "clients.view" }, // hérite de la page liste
  "/tasks/:taskId": { permission: "tasks.view" }, // hérite de la page liste
  /**
   * Spec Équipe — « Rôles & Organisation » a quitté le MENU (gestion déplacée
   * vers Paramètres > Rôles & permissions), pas la matrice v2 : la page reste
   * servie pour ne pas casser les liens existants. Sans NAV_ITEMS elle
   * retomberait sur la règle vide (ouverte à tous) : on redéclare donc
   * explicitement l'héritage de `/team`. Elle n'est PAS dans REMOVED_ROUTES,
   * qui bloquerait aussi les MANAGER y ayant accès aujourd'hui.
   */
  "/team/organisation": { permission: "team.view" },
  "/team/:memberId": {}, // auto-consultation indécidable côté client — le backend décide
  "/profile": {}, // page personnelle
  "/notifications": {}, // page personnelle
};

/** Règles dérivées du menu : mêmes clés permission/adminOnly/restrictRoles que le Sidebar. */
const NAV_RULES: Record<string, RouteRule> = Object.fromEntries(
  NAV_ITEMS.map((item) => [
    item.to,
    {
      ...(item.permission ? { permission: item.permission } : {}),
      ...(item.adminOnly ? { adminOnly: true } : {}),
      ...(item.restrictRoles ? { restrictRoles: item.restrictRoles } : {}),
    },
  ]),
);

/**
 * Résout la règle d'accès d'un chemin de route (motif react-router, avec le
 * `/` initial, ex. "/clients/:clientId"). Un chemin inconnu → règle vide
 * (autorisé), pour ne pas casser les routes techniques (index, wildcard).
 */
export function getRouteRule(path: string): RouteRule {
  if (REMOVED_ROUTES.has(path)) return { removed: true };
  return EXTRA_ROUTE_RULES[path] ?? NAV_RULES[path] ?? {};
}

/**
 * Évalue l'accès d'un utilisateur à une règle — miroir exact du filtre du
 * Sidebar : `(!permission || can) && (!adminOnly || isAgencyAdmin) &&
 * (!restrictRoles || roles ∩ restrictRoles)`.
 */
export function evaluateRouteAccess(
  user: CurrentUser | null | undefined,
  rule: RouteRule,
): RouteAccess {
  if (rule.removed) {
    // Portes de service OWNER/ADMIN pendant la transition RBAC v2 (§8).
    const isSuper = !!user && user.roles.some((r) => r === "OWNER" || r === "ADMIN");
    return isSuper ? "allowed" : "removed";
  }
  if (!rule.permission && !rule.adminOnly && !rule.restrictRoles) return "allowed";
  if (!user) return "forbidden";
  if (rule.adminOnly && !isAgencyAdmin(user)) return "forbidden";
  if (rule.restrictRoles && !rule.restrictRoles.some((r) => (user.roles as string[]).includes(r))) {
    return "forbidden";
  }
  if (rule.permission && !can(user, rule.permission)) return "forbidden";
  return "allowed";
}
